// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { vi } from './vi.js';
import { en } from './en.js';
import { da } from './da.js';
import { sv } from './sv.js';
import { nb } from './nb.js';
import { nn } from './nn.js';
import { de } from './de.js';
import { fi } from './fi.js';
import { nl } from './nl.js';
import { fr } from './fr.js';
import { lb } from './lb.js';
import { ga } from './ga.js';
import { es } from './es.js';
import { ca } from './ca.js';
import { eu } from './eu.js';
import { pt } from './pt.js';
/**
 * fix-sprint-20260716-2116-followup — Forseti workflow named-fallback contract.
 *
 * Sprint 47.5.0 authored the Forseti workflow surfaces in EN and VI. The
 * other 14 locales fall back to EN via this contract until sprint 58.6.0
 * (docs/product/phases/phase-25/sprint-58.6.0.md) lands authored per-locale
 * translations. Same shape and intent as `REGULATORY_MODEL_FALLBACK_KEYS`,
 * `HELP_CONTENT_PACKS_FALLBACK`, `WORKSPACE_SECTION_FALLBACK_KEYS` — a
 * named, greppable data structure rather than a bespoke function that
 * "exists specifically to make the parity gate pass" (the concern the
 * earlier bespoke `applyForsetiWorkflowFallback` was flagged for).
 *
 * Once sprint 58.6.0 lands real translations, `??=` becomes a no-op and a
 * housekeeping sprint can retire the contract entries whose leaves are all
 * authored.
 */
const FORSETI_WORKFLOW_FALLBACK_KEYS = {
    'forseti.workflows': [
        'availableLabel',
        'builtinGuidance',
        'customBadge',
        'governedWorkflowLabel',
        'howItWorksHeading',
        'humanReviewLabel',
        'libraryDescription',
        'libraryEyebrow',
        'libraryOverviewLabel',
        'libraryTitle',
        'myWorkflowsLabel',
        'openWorkflow',
        'outputsHeading',
        'readyToRunHeading',
        'runCitationWarning',
        'runNoResult',
        'runResultHeading',
        'runViewEvidence',
        'syncBuiltinButton',
        'syncBuiltinError',
        'syncBuiltinRunning',
        'syncBuiltinSuccess',
        'workflowProfileHeading',
    ],
    'forseti.advisorRunPanel': [
        'workbenchLabel',
        'currentRecordLabel',
        // Sprint 63.29.0 (Q5, #540) — the on-request summary control. Real EN and
        // VI; the other locales take the English (R-57).
        'showSummary',
        'summaryLoading',
        'summaryLoadFailed',
        'summaryUnavailable',
        'summaryOnRequestHint',
    ],
    'forseti.rowAction': ['shortLabel'],
};
/**
 * fix-sprint-20260716-2116-followup — Forseti nested-subtree fallbacks.
 *
 * `forseti.workflows.practice_display` and
 * `forseti.advisorRunPanel.breachDraftResolved` are variable-shape nested
 * maps whose leaves live on `en` at authoring time. Applying them via a
 * subtree helper (rather than enumerating leaf keys) means adding a new
 * `practice_display.<label>` in `en.ts` propagates to all 14 fallback
 * locales at import time without touching this file.
 *
 * Retired by sprint 58.6.0 (docs/product/phases/phase-25/sprint-58.6.0.md),
 * whose scope names both of these subtrees explicitly
 * ("All keys defined on `en.forseti.workflows.practice_display`" and
 * "All keys defined on `en.forseti.advisorRunPanel.breachDraftResolved`").
 *
 * This contract was missed when the other four were audited — it is a
 * `*_PATHS` array rather than a `*_KEYS` map, so a scan looking for the map
 * shape did not see it. `fallback-contract-scheduling.test.ts` matches on the
 * `FALLBACK` name substring instead, and it is what found this one.
 */
const FORSETI_SUBTREE_FALLBACK_PATHS = [
    'forseti.workflows.practice_display',
    'forseti.advisorRunPanel.breachDraftResolved',
];
function applyNamedSubtreeFallback(locale, path) {
    const parts = path.split('.');
    const parentParts = parts.slice(0, -1);
    const leafKey = parts[parts.length - 1];
    let enSourceParent = en;
    let localeTargetParent = locale;
    for (const part of parentParts) {
        enSourceParent = enSourceParent?.[part];
        localeTargetParent = localeTargetParent?.[part];
        if (!enSourceParent || !localeTargetParent)
            return;
    }
    const enSubtree = enSourceParent[leafKey];
    if (!enSubtree)
        return;
    const existing = localeTargetParent[leafKey];
    if (!existing) {
        localeTargetParent[leafKey] = { ...enSubtree };
        return;
    }
    for (const key of Object.keys(enSubtree))
        existing[key] ??= enSubtree[key];
}
/**
 * fix-sprint-20260717-2118-followup — Workspace-section titles introduced
 * across 15 admin pages to replace field-labels-as-section-chrome. EN + VI
 * carry authored strings; the other 14 locales fall back to EN via this
 * named contract until sprint 58.6.1
 * (docs/product/phases/phase-25/sprint-58.6.1.md) lands authored per-locale
 * translations for all 15 leaves. Same shape and intent as
 * `REGULATORY_MODEL_FALLBACK_KEYS`.
 *
 * The sprint reference is load-bearing. "Until a localization batch supplies
 * real translations" named no batch and no owner, so the 15 leaves shipped
 * English to 14 locales indefinitely — and the deep-parity test could never
 * surface it, because this contract writes EN into the raw exports at import
 * time, so every leaf path exists in every locale and parity passes.
 */
const WORKSPACE_SECTION_FALLBACK_KEYS = {
    consolidation: ['section_case_context', 'section_review_plan'],
    'incidents.list': ['section_incident_details'],
    'org_groups.settings': ['configuration_heading'],
    'platform_pages.sod': ['section_conflict_pairs_configuration'],
    'vendor_risk.reviews': ['section_assignment', 'section_findings_recommendations'],
    'vendor_risk.contracts': ['section_contract_details'],
    'program_governance.obligation_mappings': ['section_ownership'],
    'program_governance.evidence_gaps': ['section_ownership_timeline'],
    'consent_properties.properties': [
        'section_property_details',
        'section_ownership',
        'section_withdrawal_detail',
    ],
    'data_mapping.flows': ['section_basic_info', 'section_narrative'],
};
/**
 * Manual consolidation section-assignment labels — the "Assign Department"
 * modal on the consolidation case detail page, which exposes the backend's
 * `POST /consolidation/:caseId/sections` route for departments that auto-assign
 * skips (no `head_user_id`) — plus the department-head field on the Departments
 * admin page, which is where that `head_user_id` is set in the first place.
 * The two ship together because a modal that explains "no departments have
 * heads" is useless without a way to give one a head.
 *
 * EN and VI are authored; the other 14 locales fall back to EN via this named
 * contract until sprint 58.6.1
 * (docs/product/phases/phase-25/sprint-58.6.1.md) lands authored per-locale
 * translations for all 14 leaves. Same shape and intent as
 * `WORKSPACE_SECTION_FALLBACK_KEYS` above; 58.6.1's scope table names this
 * contract explicitly, so the leaves have a batch and an owner rather than an
 * unnamed "next localization batch".
 */
const CONSOLIDATION_ASSIGNMENT_FALLBACK_KEYS = {
    consolidation: [
        'assign_department',
        'assign_dept_modal_title',
        'field_section_title',
        'field_section_description',
        'field_assignee',
        'select_department_placeholder',
        'select_assignee_placeholder',
        'section_assigned',
    ],
    'consolidation.errors': ['assign_missing_fields', 'assign_failed', 'no_departments_with_heads'],
    'admin.organizations.departments.columns': ['head_user'],
    'admin.organizations.departments.form': ['head_user', 'head_user_none'],
};
/**
 * fix-sprint-20260827-2129 F18 — consolidation case and section FAILURE text.
 *
 * `ConsolidationCaseDetailPage` ended four catch blocks in
 * `setError(err?.message || '<hardcoded English>')`, rendered into the
 * page-level `role="alert"` region. A Vietnamese operator whose transition
 * failed without a server message read — and had announced to them — English.
 * EN and VI are authored; these leaves join the same named, owned contract
 * the four above them use. Ten leaves since the 2129 residue pass added the
 * review, section and export pages' six.
 *
 * Deliberately NOT folded into `CONSOLIDATION_ASSIGNMENT_FALLBACK_KEYS`, whose
 * docblock scopes it to the "Assign Department" modal and the department-head
 * field. These are case-transition and section save/submit failures — a
 * different surface with a different owner — and widening that contract to hold
 * them would have made its own description untrue. Naming a second contract
 * costs four lines; a docblock that no longer describes what it covers is the
 * defect class this whole fix sprint is about.
 *
 * Falls back to EN in the other 14 locales until sprint 58.6.1
 * (docs/product/phases/phase-25/sprint-58.6.1.md), whose scope table counts
 * these ten.
 */
const CONSOLIDATION_FAILURE_FALLBACK_KEYS = {
    'consolidation.errors': [
        'transition_failed',
        'auto_assign_failed',
        'save_failed',
        'submit_failed',
        // fix-sprint-20260827-2129 residue (B-F18 class) — the same surface's other
        // pages: `ConsolidationReviewPage` (accept, return, flag action),
        // `SectionInputPage` (comment, evidence) and `DossierExportPage` (export)
        // ended their catch blocks the same way. Ten leaves now; 58.6.1 counts ten.
        'accept_failed',
        'return_failed',
        'flag_action_failed',
        'comment_failed',
        'evidence_failed',
        'export_failed',
    ],
};
/**
 * fix-sprint-20260827-2129 residue (B-F18 class) — localization page FAILURE
 * text.
 *
 * Eight localization pages (market packs catalogue and detail, language packs,
 * dashboard, audit, translation preview, pack submissions, module mappings)
 * ended their catch blocks in `err?.message || '<hardcoded English>'`, so a
 * Vietnamese operator whose request failed without a server message read — and
 * in the page-level alerts had announced to them — English. EN and VI are
 * authored under `localization.errors`; the 14 other locales receive the EN
 * text here (R-57: locale work beyond EN/VI defers to Phase 25).
 *
 * A subtree contract (`applyNamedSubtreeFallback`) because `localization.errors`
 * does not exist in the 14 files at all; the section form requires the target
 * object to be there and would silently skip.
 *
 * Retired by sprint 58.6.1 (docs/product/phases/phase-25/sprint-58.6.1.md),
 * whose scope table names this contract and its 24 leaves.
 */
const LOCALIZATION_FAILURE_FALLBACK_PATH = 'localization.errors';
/**
 * Sprint 61.0.11, gate E12.5 pass 11 (reviewer A, P3-4) — the file-policy
 * save-conflict pair.
 *
 * `platform.files_config.save_version_conflict` used to end *"Reload to see the
 * latest values."* — a remedy the page does not offer, which is the defect class
 * this whole sprint is about. EN and VI were rewritten: the page now re-reads the
 * saved version on a conflict without touching the drafts, so the sentence says
 * what actually happened and what to press. **The other 14 locales still carried
 * a translation of the old sentence**, so a Danish or German operator was told to
 * reload, by a toast with no control beside it, for a page that had stopped
 * needing one. A stale translation of a withdrawn promise is still the promise.
 *
 * `save_version_conflict_unreadable` is the same pair's other half, added earlier
 * in this sprint. It went in as inline EN text in all 14 files under a bare
 * one-line `R-57 — structural parity now, translation in Phase 25` comment — which
 * is the mechanism this file exists to replace: an
 * EN string standing in for a missing translation is acceptable only when it is
 * NAMED as such, and an inline copy is named by nothing.
 * `fallback-contract-scheduling.test.ts` cannot see it, because it only reads
 * contracts declared here, so those 14 leaves were scheduled by no sprint and no
 * owner — exactly the state that shipped 67 leaves of English indefinitely and
 * caused that guard to be written. Both keys are therefore removed from the 14
 * files and named here together; the pair cannot be split, because the two
 * sentences differ by one clause and a reviewer needs to see them side by side.
 *
 * Falls back to EN in the other 14 locales until sprint 58.6.1
 * (docs/product/phases/phase-25/sprint-58.6.1.md), whose scope table counts
 * these two.
 */
const FILES_CONFIG_SAVE_CONFLICT_FALLBACK_KEYS = {
    'platform.files_config': ['save_version_conflict', 'save_version_conflict_unreadable'],
};
/**
 * Sprint 62.0.3 — non-Vietnam regulatory model leaves.
 *
 * EN and VI are authored; the other 14 locales fall back to EN until sprint
 * 58.6.1 (docs/product/phases/phase-25/sprint-58.6.1.md) lands authored
 * per-locale translations for all 23 leaves, following the same narrow, named
 * contract as `FORSETI_WORKFLOW_FALLBACK_KEYS` above.
 *
 * fix-sprint-20260907-2133 F6 — it said 20 until this fix. Sprint 65.9.0 added
 * `clock_lapse_label`, `clock_lapse_sanction_exposure` and
 * `clock_lapse_right_extinguished` under `incidents.breach_notifications` and
 * moved neither this number nor 58.6.1's, so 3 leaves × 14 locales = 42 newly
 * deferred strings were scheduled by nothing and 58.6.1 could have closed as
 * written while leaving them English. R-57: deferral is never silent. The
 * sibling test now derives this count from the object below and compares it
 * with the retiring spec's, so a contract cannot grow quietly again. Runtime lookup deep-merges
 * over `en`, so this exists for the raw exports the parity checks and
 * build-time tools consume. Existing translated values always win (`??=`), so
 * adding real translations later needs no change here.
 *
 * Listed explicitly rather than blanket-copied: an EN string standing in for a
 * missing translation is only acceptable when it is *named* as such, which is
 * what makes the eventual localization batch able to find them.
 */
const REGULATORY_MODEL_FALLBACK_KEYS = {
    'incidents.breach_notifications': [
        'regulatory_clocks',
        'no_regulatory_clocks',
        'clock_counts_from',
        'clock_undated',
        'clock_no_period',
        'clock_provisional',
        'clock_lapse_label',
        'clock_lapse_sanction_exposure',
        'clock_lapse_right_extinguished',
        'clock_trigger_awareness',
        'clock_trigger_discovery',
        'clock_trigger_containment',
        'clock_trigger_authority_request',
        'clock_trigger_assessment_complete',
        'clock_trigger_framework_specific',
        // Sprint 63.8.0, D10 — the frame shown when a pack authors a trigger this
        // platform does not model. Listed here rather than translated into the 14,
        // per R-57: the EN string stands in and is NAMED as standing in, so the
        // Phase 25 batch can find it.
        'clock_trigger_unrecognised',
        'clock_provisional_reconstructed_trigger_timestamp',
        'clock_provisional_disputed_trigger_timestamp',
        'clock_provisional_transposition_unavailable',
    ],
    'localization.frameworks_detail': [
        'no_filing_authority_title',
        'no_filing_authority_body',
        'no_filing_authority_basis_certification_body',
        'no_filing_authority_basis_commercially_engaged_auditor',
        'no_filing_authority_basis_internal_governance_only',
    ],
};
// fix-sprint-20260716-2116-followup — retired the bespoke
// `applyRegulatoryModelFallback` function; `REGULATORY_MODEL_FALLBACK_KEYS`
// is now consumed by the general `applyNamedSectionFallback` in the loop
// below, alongside the three other named contracts.
/**
 * fix-sprint-20260719-0024-followup — HelpContentPacksPage subtree fallback.
 *
 * The page was fully hardcoded English before its authoring sprint; converting
 * it to `t.localization.help_content_packs.*` introduces 32 new leaf paths,
 * and the deep-parity test asserts every leaf path exists in every non-EN
 * locale. EN and VI carry authored strings; the other 14 locales receive the
 * EN text via `applyNamedSubtreeFallback` until sprint 58.6.1
 * (docs/product/phases/phase-25/sprint-58.6.1.md) lands authored per-locale
 * translations for all 32 leaves. Same contract shape as the Forseti subtree
 * paths above.
 *
 * Measured at the time 58.6.1 was specced: all 32 leaves are English-identical
 * in all 14 fallback locales.
 */
const HELP_CONTENT_PACKS_FALLBACK_PATH = 'localization.help_content_packs';
/**
 * Sprint 62.20.0, gate E12.5 pass 2 (P2-1) — the registry export's draft frame.
 *
 * Two leaves (`marker`, `scheme_label`) that let the regulator-facing register
 * export say a tier came from an instrument which does not bind. EN and VI
 * carry authored strings — the reviewed wording the on-screen
 * `DraftInstrumentNotice` already uses — and the other 14 locales receive the
 * EN text here (R-57: locale work beyond EN/VI defers to Phase 25).
 *
 * Retired by sprint 58.6.1 (docs/product/phases/phase-25/sprint-58.6.1.md),
 * whose scope table names this contract and its 2 leaves.
 *
 * fix-sprint-20260904-2120 F3 — that citation is the whole point of the
 * docblock and it was missing. R-57 says deferral is never silent; this was
 * the only one of the seven contracts naming no retiring sprint, so its
 * 2 leaves × 14 locales = 28 deferred strings were tracked by nothing.
 * `fallback-contract-scheduling.test.ts` passed anyway because its sprint
 * regex matched "Sprint 62.20.0" in the line above — the sprint that CREATED
 * the contract, read as the sprint that retires it. That check now resolves
 * the cited spec on disk and requires it to name the contract, so a
 * self-reference cannot satisfy it.
 *
 * `applyNamedSubtreeFallback` rather than `applyNamedSectionFallback`: the
 * subtree does not exist in the 14 files at all, and the section form requires
 * the target object to be there already, so it would silently skip and leave
 * the deep-parity gate to find it.
 */
const REGISTRY_EXPORT_DRAFT_FALLBACK_PATH = 'data_mapping.registry.export_draft';
function applyNamedSectionFallback(locale, contract) {
    for (const [sectionPath, keys] of Object.entries(contract)) {
        const parts = sectionPath.split('.');
        let source = en;
        let target = locale;
        for (const part of parts) {
            source = source?.[part];
            target = target?.[part];
            if (!source || !target)
                break;
        }
        if (!source || !target)
            continue;
        for (const key of keys)
            target[key] ??= source[key];
    }
}
/**
 * Every named fallback contract above, applied to one non-EN locale bundle in place (`??=`: a translated value always
 * wins). The loop below applies it to the 15 shipped bundles at import time; `scripts/i18n-check.ts` and its test
 * (fix-sprint-20261031-1800 item 5) apply it to a fixture, so the completeness report reads the bundles as the app
 * does and still names a key removed from a locale that no contract fills.
 */
export function applyNamedFallbackContracts(locale) {
    const target = locale;
    applyNamedSectionFallback(target, FORSETI_WORKFLOW_FALLBACK_KEYS);
    for (const path of FORSETI_SUBTREE_FALLBACK_PATHS)
        applyNamedSubtreeFallback(target, path);
    applyNamedSectionFallback(target, REGULATORY_MODEL_FALLBACK_KEYS);
    applyNamedSubtreeFallback(target, HELP_CONTENT_PACKS_FALLBACK_PATH);
    applyNamedSubtreeFallback(target, REGISTRY_EXPORT_DRAFT_FALLBACK_PATH);
    applyNamedSubtreeFallback(target, LOCALIZATION_FAILURE_FALLBACK_PATH);
    applyNamedSectionFallback(target, WORKSPACE_SECTION_FALLBACK_KEYS);
    applyNamedSectionFallback(target, CONSOLIDATION_ASSIGNMENT_FALLBACK_KEYS);
    applyNamedSectionFallback(target, CONSOLIDATION_FAILURE_FALLBACK_KEYS);
    applyNamedSectionFallback(target, FILES_CONFIG_SAVE_CONFLICT_FALLBACK_KEYS);
}
for (const locale of [vi, da, sv, nb, nn, de, fi, nl, fr, lb, ga, es, ca, eu, pt]) {
    applyNamedFallbackContracts(locale);
}
export { vi, en, da, sv, nb, nn, de, fi, nl, fr, lb, ga, es, ca, eu, pt };
export { translateEnum } from './translate-enum.js';
const translations = {
    // Vietnamese
    'vi-VN': vi,
    vi: vi,
    // English
    'en-US': en,
    en: en,
    'en-IE': en,
    'en-GB': en,
    // Danish
    'da-DK': da,
    da: da,
    // Swedish
    'sv-SE': sv,
    'sv-FI': sv,
    sv: sv,
    // Norwegian Bokmål
    'nb-NO': nb,
    nb: nb,
    // Norwegian Nynorsk
    'nn-NO': nn,
    nn: nn,
    // German
    'de-DE': de,
    'de-AT': de,
    'de-LU': de,
    'de-BE': de,
    de: de,
    // Finnish
    'fi-FI': fi,
    fi: fi,
    // Dutch
    'nl-NL': nl,
    'nl-BE': nl,
    nl: nl,
    // French
    'fr-FR': fr,
    'fr-BE': fr,
    'fr-LU': fr,
    fr: fr,
    // Luxembourgish
    'lb-LU': lb,
    lb: lb,
    // Irish
    'ga-IE': ga,
    ga: ga,
    // Spanish
    'es-ES': es,
    es: es,
    // Catalan
    'ca-ES': ca,
    ca: ca,
    // Basque
    'eu-ES': eu,
    eu: eu,
    // Portuguese
    'pt-PT': pt,
    pt: pt,
};
/** Deep-merge two objects, using `base` values wherever `overlay` is missing. */
function deepMerge(base, overlay) {
    const result = { ...base };
    for (const key of Object.keys(overlay)) {
        const bVal = base[key];
        const oVal = overlay[key];
        if (typeof bVal === 'object' &&
            bVal !== null &&
            typeof oVal === 'object' &&
            oVal !== null &&
            !Array.isArray(bVal)) {
            result[key] = deepMerge(bVal, oVal);
        }
        else {
            result[key] = oVal;
        }
    }
    return result;
}
const enBase = en;
/**
 * Sprint 64.70.0 — memoised per locale.
 *
 * This function is not a lookup. For every locale except `en-US` it performed a
 * full `deepMerge` of the entire bundle **on every call**, and it is called on
 * every render of `LocaleProvider`. Measured before this cache:
 *
 *   en-US   identity-stable=true    0.000 ms/call  (returns the base object)
 *   vi-VN   identity-stable=false   3.056 ms/call
 *   de-DE   identity-stable=false   2.979 ms/call
 *
 * So `t` was identity-stable **in English and nowhere else**, which is why a
 * consumer could not honestly put it in a dependency array, and why an English
 * assertion passes against the defect — the trap Sprint 64.70.0's AC2 exists to
 * close.
 *
 * A plain `Map` is correct here rather than an LRU: the key space is the 16
 * shipped locales, bounded and tiny, and a bundle is immutable once built.
 */
const translationCache = new Map();
/**
 * Number of entries currently retained by the translation cache.
 *
 * Exported for `index.test.ts` only. The bound this reports is the whole point
 * of F19: an unauthenticated caller must not be able to grow it.
 */
export function __translationCacheSize() {
    return translationCache.size;
}
/**
 * Get translation object for a given locale.
 * Deep-merges with English so any missing keys fall back to EN instead of crashing.
 *
 * The returned object is **identity-stable per locale** — repeated calls with
 * the same locale return the same reference, so it is safe in a dependency
 * array.
 */
export function getTranslations(locale) {
    // fix-sprint-20260817-2132 F19 — cache ONLY resolved locales.
    //
    // The comment above claims "the key space is the 16 shipped locales, bounded
    // and tiny". That is true of the frontend caller and false of the backend
    // one: `rights-requests/controller.ts` calls
    // `getTranslations(input.preferred_language)`, and `preferred_language` comes
    // from `publicIntakeSchema` — `z.string().min(2).max(5)`, any characters —
    // on `router.post('/intake', publicRateLimiter, …)`, which is
    // UNAUTHENTICATED. An unknown locale fell through to `en-US` and was then
    // `set()` under the caller-supplied key, so distinct 2–5-character values
    // accumulated map entries for the lifetime of the process. Before the cache
    // existed the function allocated per call and retained nothing.
    //
    // Resolving first makes the claim true: an unknown locale returns the English
    // base and writes nothing, so the key space really is the shipped locales.
    const known = translations[locale];
    if (!known)
        return enBase;
    const cached = translationCache.get(locale);
    if (cached)
        return cached;
    const merged = known === enBase ? known : deepMerge(enBase, known);
    translationCache.set(locale, merged);
    return merged;
}
//# sourceMappingURL=index.js.map